<script setup>
// The ONE icon primitive. Renders a Font Awesome <i>, nothing more — no icon
// library, no per-icon imports, no version-locked component package. Swapping
// icon systems later means editing this one file plus the <link> in index.html.
//
// Icons are decorative (aria-hidden). Meaning is always carried by an adjacent
// label or an aria-label on the control (D6, WCAG 1.4.1) — never by the glyph.
import { computed } from 'vue'

const props = defineProps({
	// Font Awesome icon name without the `fa-` prefix, e.g. "clock", "chevron-down".
	name: { type: String, required: true },

	// solid | regular | light | thin | duotone. Free ships solid (+ limited
	// regular); Pro adds light/thin, which match the concept's 1.5px stroke
	// language (D13). Default is solid so the app renders on Free out of the box.
	weight: { type: String, default: 'solid' },

	// Optional pixel size. Omit to inherit the surrounding font-size (1em).
	size: { type: [Number, String], default: null },

	// Continuous rotation (e.g. a syncing/queued state).
	spin: { type: Boolean, default: false },


	// The full Font Awesome icon name/classes. This is to allow for legacy icons in the primary nav menu definition
	// during transition to the new design system.
	fullName: { type: String, required: false },
})

const cls = computed(() => props.fullName ? props.fullName : [`fa-${props.weight}`, `fa-${props.name}`, { 'fa-spin': props.spin }])
const style = computed(() =>
	props.size ? { fontSize: typeof props.size === 'number' ? `${props.size}px` : props.size } : null,
)
</script>

<template>
	<i :class="cls" :style="style" aria-hidden="true"></i>
</template>

<style scoped>
i
{
	/* Icons inherit their colour from the surrounding text (currentColor is FA's
     default) and sit on the text baseline inside flex rows. */
	line-height: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}
</style>
